import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import StarIcon from "@mui/icons-material/Star";
import DiamondIcon from "@mui/icons-material/Diamond";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import utils from "../../universal/utils";
import {
  TIERS,
  TIER_DISPLAY,
  BILLING_CYCLES,
  SUBSCRIPTION_STATUS,
  YEARLY_RENEWAL_WINDOW_DAYS,
} from "../../constants/membership";
import { fetchMembershipStatus } from "../../redux/actions/catalogActions";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
  container: {
    border: "1px solid rgba(232, 163, 61, 0.3)",
    borderRadius: 12,
    padding: "1.5rem",
    marginBottom: "2rem",
    background: "linear-gradient(135deg, rgba(28, 30, 32, 0.9) 0%, rgba(42, 45, 48, 0.9) 100%)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1rem",
  },
  tierBadge: {
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "0.5px",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "0.5rem",
  },
  infoLabel: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.85rem",
  },
  infoValue: {
    color: "#ffffff",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  upgradeButton: {
    marginTop: "1rem",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "0.5rem 1.5rem",
  },
  adminNote: {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "0.75rem",
    fontStyle: "italic",
    marginTop: "0.5rem",
  },
  daysChip: {
    fontWeight: 600,
  },
  dangerZone: {
    marginTop: "1.25rem",
    padding: "1rem",
    borderRadius: 10,
    border: "1px solid rgba(244, 67, 54, 0.35)",
    backgroundColor: "rgba(244, 67, 54, 0.08)",
  },
  cancelledZone: {
    marginTop: "1.25rem",
    padding: "1rem",
    borderRadius: 10,
    border: "1px solid rgba(76, 175, 80, 0.35)",
    backgroundColor: "rgba(76, 175, 80, 0.08)",
  },
  zoneTitle: {
    color: "#ffffff",
    fontSize: "0.95rem",
    fontWeight: 700,
    marginBottom: "0.5rem",
  },
  zoneText: {
    color: "rgba(255, 255, 255, 0.72)",
    fontSize: "0.85rem",
    lineHeight: 1.5,
  },
  renewalZone: {
    marginTop: "1.25rem",
    padding: "1rem",
    borderRadius: 10,
    border: "1px solid rgba(255, 193, 7, 0.35)",
    backgroundColor: "rgba(255, 193, 7, 0.08)",
  },
  renewButton: {
    marginTop: "0.9rem",
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 8,
  },
  cancelButton: {
    marginTop: "0.9rem",
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 8,
  },
  dialogPaper: {
    "& .MuiDialog-paper": {
      borderRadius: 12,
      backgroundColor: "#1c1e20",
      color: "#ffffff",
      maxWidth: 520,
    },
  },
  dialogText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "0.92rem",
    lineHeight: 1.5,
  },
}));

export default function MembershipSection() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useSelector((state) => state.catalogReducer?.user);
  const membership = useSelector((state) => state.catalogReducer?.membership);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(null);

  useEffect(() => {
    if (user?.userId) {
      dispatch(fetchMembershipStatus(user.userId));
    }
  }, [user?.userId, dispatch]);

  const tier = membership?.tier || TIERS.FREE;
  const tierInfo = TIER_DISPLAY[tier] || TIER_DISPLAY[TIERS.FREE];
  const isFreeTier = tier === TIERS.FREE;
  const isAdminGranted = membership?.grantedByAdmin;
  const isMonthlyMembership = membership?.billingCycle === BILLING_CYCLES.MONTHLY;
  const isYearlyMembership = membership?.billingCycle === BILLING_CYCLES.YEARLY;
  const isRenewalCancelled = membership?.subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED || Boolean(cancelSuccess);
  const canRenewYearly = Boolean(membership?.yearlyRenewalEligible);
  const formattedAccessEndDate = useMemo(() => {
    if (!membership?.expiresAt) return null;
    return new Date(membership.expiresAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [membership?.expiresAt]);

  const getTierIcon = () => {
    if (tier === TIERS.PRO) return <DiamondIcon sx={{ fontSize: "1rem" }} />;
    if (tier === TIERS.PREMIUM) return <StarIcon sx={{ fontSize: "1rem" }} />;
    return null;
  };

  const handleCancelRenewal = async () => {
    if (!user?.userId) return;

    setIsCancelling(true);
    setCancelError(null);

    try {
      const res = await fetch(`${basePath}/api/v1/membership/cancel`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();

      if (!data.ok) {
        setCancelError(data.message || "Failed to cancel renewal");
        return;
      }

      setCancelSuccess(
        formattedAccessEndDate
          ? `Recurring billing cancelled. Your ${tierInfo.name} access stays active until ${formattedAccessEndDate}.`
          : "Recurring billing cancelled. Access remains active until the end of your current period."
      );
      setCancelDialogOpen(false);
      dispatch(fetchMembershipStatus(user.userId));
    } catch (err) {
      setCancelError(err.message || "Network error");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", fontSize: "1.1rem" }}>
          Membership
        </Typography>
        <Chip
          icon={getTierIcon()}
          label={tierInfo.name}
          className={classes.tierBadge}
          sx={{
            backgroundColor: `${tierInfo.color}22`,
            color: tierInfo.color,
            borderColor: tierInfo.color,
            border: "1px solid",
          }}
        />
      </Box>

      {!isFreeTier && (
        <>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Plan:</Typography>
            <Typography className={classes.infoValue}>{tierInfo.name}</Typography>
          </Box>

          {membership?.billingCycle && (
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Billing:</Typography>
              <Typography className={classes.infoValue}>
                {membership.billingCycle === BILLING_CYCLES.YEARLY ? "Yearly" : "Monthly"}
                {membership.billingCycle === BILLING_CYCLES.MONTHLY
                  ? isRenewalCancelled
                    ? " (renewal cancelled)"
                    : " (recurring via PayPal)"
                  : ""}
              </Typography>
            </Box>
          )}

          {membership?.daysRemaining != null && membership.daysRemaining > 0 && (
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>
                {membership?.billingCycle === BILLING_CYCLES.MONTHLY && !isRenewalCancelled
                  ? "Next billing:"
                  : "Days remaining:"}
              </Typography>
              <Chip
                label={`${membership.daysRemaining} days`}
                size="small"
                className={classes.daysChip}
                sx={{
                  backgroundColor: membership.daysRemaining > 7 ? "rgba(76, 175, 80, 0.2)" : "rgba(255, 152, 0, 0.2)",
                  color: membership.daysRemaining > 7 ? "#4CAF50" : "#FF9800",
                }}
              />
            </Box>
          )}

          {membership?.expiresAt && (
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>
                {membership?.billingCycle === BILLING_CYCLES.MONTHLY
                  ? isRenewalCancelled
                    ? "Access ends:"
                    : "Current period ends:"
                  : "Expires on:"}
              </Typography>
              <Typography className={classes.infoValue}>
                {formattedAccessEndDate}
              </Typography>
            </Box>
          )}

          {!isAdminGranted && isYearlyMembership && (
            canRenewYearly ? (
              <Box className={classes.renewalZone}>
                <Typography className={classes.zoneTitle}>Renew for another year</Typography>
                <Typography className={classes.zoneText}>
                  You are inside the renewal window. Paying now extends your current {tierInfo.name}
                  membership by another year without removing your remaining time.
                </Typography>
                <Button
                  variant="outlined"
                  className={classes.renewButton}
                  onClick={() => history.push("/membership/plans?billingCycle=yearly&renew=yearly")}
                  sx={{
                    borderColor: "#FFC107",
                    color: "#FFC107",
                    "&:hover": {
                      borderColor: "#FFC107",
                      backgroundColor: "rgba(255, 193, 7, 0.08)",
                    },
                  }}
                >
                  Renew yearly plan
                </Button>
              </Box>
            ) : membership?.daysRemaining > YEARLY_RENEWAL_WINDOW_DAYS ? (
              <Typography className={classes.adminNote}>
                Yearly renewal opens during the final {YEARLY_RENEWAL_WINDOW_DAYS} days before expiration.
              </Typography>
            ) : null
          )}

          {!isAdminGranted && isMonthlyMembership && (
            <>
              {cancelError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {cancelError}
                </Alert>
              )}

              {cancelSuccess && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  {cancelSuccess}
                </Alert>
              )}

              {isRenewalCancelled ? (
                <Box className={classes.cancelledZone}>
                  <Typography className={classes.zoneTitle}>Renewal cancelled</Typography>
                  <Typography className={classes.zoneText}>
                    No further PayPal payments will be collected.
                    {formattedAccessEndDate
                      ? ` Your ${tierInfo.name} membership stays active until ${formattedAccessEndDate}.`
                      : " Your current membership remains active until the end of the paid period."}
                  </Typography>
                </Box>
              ) : (
                <Box className={classes.dangerZone}>
                  <Typography className={classes.zoneTitle}>Danger zone</Typography>
                  <Typography className={classes.zoneText}>
                    Stop the next PayPal payment now while keeping your current access until the
                    end of this billing period.
                    {formattedAccessEndDate
                      ? ` Your membership will stay active until ${formattedAccessEndDate}.`
                      : ""}
                  </Typography>
                  <Button
                    variant="outlined"
                    className={classes.cancelButton}
                    onClick={() => {
                      setCancelError(null);
                      setCancelDialogOpen(true);
                    }}
                    sx={{
                      borderColor: "#F44336",
                      color: "#F44336",
                      "&:hover": {
                        borderColor: "#F44336",
                        backgroundColor: "rgba(244, 67, 54, 0.08)",
                      },
                    }}
                  >
                    Cancel next payment
                  </Button>
                </Box>
              )}
            </>
          )}

          {isAdminGranted && (
            <Typography className={classes.adminNote}>
              This membership was granted by the Ouro team.
            </Typography>
          )}

          {!isAdminGranted && (
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              {tier === TIERS.PREMIUM && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => history.push("/membership/plans")}
                  sx={{
                    textTransform: "none",
                    borderColor: "#FFD700",
                    color: "#FFD700",
                    "&:hover": { borderColor: "#FFD700", backgroundColor: "rgba(255, 215, 0, 0.08)" },
                  }}
                >
                  Upgrade to Pro
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {isFreeTier && (
        <>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              You are on the Free plan. Upgrade for advanced matchmaking settings and tighter control over maps and server locations.
            </Typography>
          </Box>

          <Button
            variant="contained"
            className={classes.upgradeButton}
            endIcon={<ArrowForwardIcon />}
            onClick={() => history.push("/membership/plans")}
            sx={{
              background: "linear-gradient(135deg, #E8A33D 0%, #E8A33D 100%)",
              color: "#ffffff",
              "&:hover": { background: "linear-gradient(135deg, #E66A00 0%, #E68500 100%)" },
            }}
          >
            View Plans & Upgrade
          </Button>
        </>
      )}

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        className={classes.dialogPaper}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel monthly renewal?</DialogTitle>
        <DialogContent>
          <Typography className={classes.dialogText} sx={{ mb: 1.5 }}>
            This will stop future PayPal charges for your membership.
          </Typography>
          <Typography className={classes.dialogText} sx={{ mb: 1.5 }}>
            {formattedAccessEndDate
              ? `You will keep your current ${tierInfo.name} access until ${formattedAccessEndDate}.`
              : `You will keep your current ${tierInfo.name} access until the end of the current billing period.`}
          </Typography>
          <Typography className={classes.dialogText}>
            After that, your account will return to the Free plan unless you subscribe again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isCancelling}
            sx={{ textTransform: "none", color: "rgba(255, 255, 255, 0.7)" }}
          >
            Keep membership
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelRenewal}
            disabled={isCancelling}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: "#F44336",
              "&:hover": {
                backgroundColor: "#D32F2F",
              },
            }}
          >
            {isCancelling ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Cancel renewal"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
